import { useMutation } from "@tanstack/react-query"
import forgotPassword from "api/user/forgotPassword"
import resetPassword from "api/user/resetPassword"
import verifyOTP from "api/user/verifyOTP"
import useValidate from "hooks/useValidate"
import { useRouter } from "next/router"
import { createContext, useContext, useReducer } from "react"

interface Props {
    children: React.ReactNode
}

interface IState {
    form: {
        email: string
        password: string
        confirm_password: string
        otp: string[]
        step: "email" | "otp"
    }
    errorForm: {
        email: string
        password: string
        confirm_password: string
        otp: string
    }
    restartTimer: boolean
    onChange: (key: string, value: string) => void
    requestOTP: (e: { preventDefault: () => void }) => void
    setRestartTimer: (value: boolean) => void
    sendOTP: (e: { preventDefault: () => void }) => void
    isLoading: boolean
    OTPVerified: boolean
    resetUserPassword: (e: { preventDefault: () => void }) => void
    isRequestLoading: boolean
}

interface IAction {
    type: string
    payload?: any
}

const initialState: IState = {
    form: {
        email: "",
        password: "",
        confirm_password: "",
        otp: new Array(4).fill(""),
        step: "email",
    },
    errorForm: {
        email: "",
        password: "",
        confirm_password: "",
        otp: "",
    },
    restartTimer: false,
    onChange: () => {},
    requestOTP: () => {},
    setRestartTimer: () => {},
    sendOTP: () => {},
    isLoading: false,
    OTPVerified: false,
    resetUserPassword: () => {},
    isRequestLoading: false,
}

function reducer(state: IState, action: IAction): IState {
    switch (action.type) {
        case "SET_FORM":
            return { ...state, form: { ...state.form, ...action.payload } }
        case "SET_ERROR":
            return {
                ...state,
                errorForm: { ...state.errorForm, ...action.payload },
            }
        case "SET_RESTART_TIMER":
            return { ...state, restartTimer: action.payload }
        default:
            return state
    }
}

const ResetPasswordContext = createContext<IState>(initialState)

const ResetPasswordProvider: React.FC<Props> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const { push } = useRouter()
    const { isEmailValid } = useValidate(state.form)

    const {
        mutate: verifyMutate,
        isPending: isOTPLoading,
        data: OTPData,
    } = useMutation({ mutationFn: verifyOTP })

    const { mutate: requestMutate, isPending: isRequestLoading } = useMutation({
        mutationFn: forgotPassword,
    })

    const { mutate: resetPasswordMutate, isPending: isResetPasswordLoading } =
        useMutation({ mutationFn: resetPassword })

    const onChange = (key: string, value: string) => {
        dispatch({ type: "SET_FORM", payload: { [key]: value } })
        dispatch({ type: "SET_ERROR", payload: { [key]: "" } })
    }

    const requestOTP = (e: { preventDefault: () => void }) => {
        e.preventDefault()

        if (!isEmailValid.test(state.form.email)) {
            dispatch({
                type: "SET_ERROR",
                payload: { email: "email_valid_err" },
            })
        } else {
            requestMutate(
                {
                    email: state.form.email,
                    userType: "user",
                },
                {
                    onSuccess: (data) => {
                        if (data) {
                            if (data.message === "checkEmailForOTP") {
                                dispatch({
                                    type: "SET_FORM",
                                    payload: { step: "otp" },
                                })
                            }
                            if (data.message === "emailNotExisted") {
                                dispatch({
                                    type: "SET_ERROR",
                                    payload: { email: "emailNotExisted" },
                                })
                            }
                        }
                    },
                }
            )
            dispatch({ type: "SET_RESTART_TIMER", payload: false })
        }
    }

    const sendOTP = (e: { preventDefault: () => void }) => {
        e.preventDefault()

        verifyMutate(
            {
                email: state.form.email,
                otp: state.form.otp.join(""),
            },
            {
                onSuccess: (data) => {
                    if (data) {
                        if (data.message === "invalidOTP") {
                            dispatch({
                                type: "SET_ERROR",
                                payload: { otp: "invalidOTP" },
                            })
                        }
                        if (data.message === "Success") {
                            dispatch({
                                type: "SET_ERROR",
                                payload: { otp: "" },
                            })
                        }
                    }
                },
            }
        )
    }

    const resetUserPassword = (e: { preventDefault: () => void }) => {
        e.preventDefault()

        if (state.form.password !== state.form.confirm_password) {
            dispatch({
                type: "SET_ERROR",
                payload: {
                    password: "password_not_match",
                    confirm_password: "password_not_match",
                },
            })
        } else if (
            state.form.password.length < 8 ||
            state.form.confirm_password.length < 8
        ) {
            dispatch({
                type: "SET_ERROR",
                payload: {
                    password: "password_valid_err",
                    confirm_password: "password_valid_err",
                },
            })
        } else {
            resetPasswordMutate(
                {
                    email: state.form.email,
                    otp: state.form.otp.join(""),
                    userType: "user",
                    password: state.form.password,
                },
                {
                    onSuccess: (data) => {
                        if (data && data.message === "operationSuccessful") {
                            push("/user/confirmed-password")
                        }
                    },
                }
            )
        }
    }

    return (
        <ResetPasswordContext.Provider
            value={{
                form: state.form,
                errorForm: state.errorForm,
                onChange,
                requestOTP,
                sendOTP,
                isRequestLoading,
                restartTimer: state.restartTimer,
                setRestartTimer: (value: boolean) =>
                    dispatch({ type: "SET_RESTART_TIMER", payload: value }),
                isLoading: isResetPasswordLoading || isOTPLoading,
                OTPVerified: OTPData?.message === "Success" ? true : false,
                resetUserPassword,
            }}>
            {children}
        </ResetPasswordContext.Provider>
    )
}

// user context custom hook
export const useResetPasswordContext = () => {
    return useContext(ResetPasswordContext)
}

export { ResetPasswordContext, ResetPasswordProvider }
